// /memories — 精彩回顧(V4P-6,自 ls-v4 Memories/相簿集錦移植的產品設計)。
// 純前端聚合:近 7 天「人形+門鈴」事件(REST 既有 per-device events API,
// 平台零改動),按日分組、卡片點擊 → 回放 seek(同 EventTimeline 慣例
// /playback/{id}?t={event_ts})。縮圖沿 16 §4.1 定版規則:snapshot 缺口
// 落地前用漸層 placeholder + 型別 icon。
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { formatRelativeUs } from '../../lib/format';
import { useDeviceList } from '../devices/useDeviceList';
import { deviceName, gradientIndex } from '../devices/deviceModel';
import { clusterOfEvent, fetchDeviceEvents, type EventItem } from './eventsLib';
import '../devices/devices.css';
import './events.css';

const WINDOW_DAYS = 7;
/**
 * 精選只收「人形與門鈴時刻」（與本頁的說明文字 `events.memoriesIntroBefore` 一致）。
 *
 * ⚠️ **2026-08-21 起這個集合的實際涵蓋範圍變小了**，而程式碼一個字都沒改：先前
 * `0x8041 Object Recognition` 整群的 key 都是 `'person'`，所以**車輛／動物／包裹事件
 * 也一直被收進精選**；分類依子序號拆開之後它們有了自己的 key，就不再落進來。
 * 以 dev 帳號近 7 天為例約少 79 筆（44 車 + 35 動物）。
 *
 * 這是**分類修正的連帶效果，不是本輪刻意的產品決定**。目前的選擇是「照本頁自己的
 * 文案辦」——文案寫的就是人形與門鈴。要不要把車／動物／包裹也算成「精彩時刻」
 * 屬產品裁量，待業主裁決；要放進來就把 key 加進這個集合並同步改文案，
 * **不要**回頭去把 `clusterOf` 的分類合併回去。
 */
const HIGHLIGHT_KEYS = new Set(['person', 'doorbell']);

interface Highlight {
  deviceId: string;
  ev: EventItem;
}

function dayKey(tsUs: number): string {
  return new Date(tsUs / 1000).toLocaleDateString('zh-Hant', { month: 'long', day: 'numeric', weekday: 'short' });
}

export default function MemoriesPage() {
  const { t } = useTranslation();
  const { devices } = useDeviceList();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Highlight[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!devices) return;
    let alive = true;
    const from = Date.now() * 1000 - WINDOW_DAYS * 24 * 3600 * 1_000_000;
    Promise.all(
      devices.map((d) =>
        fetchDeviceEvents(d.device_id, { from, limit: 100 })
          .then((res) => res.items.map((ev) => ({ deviceId: d.device_id, ev })))
          .catch(() => [] as Highlight[]) // 單台失敗不拖垮集錦
      )
    )
      .then((all) => {
        if (!alive) return;
        const flat = all
          .flat()
          .filter((h) => HIGHLIGHT_KEYS.has(clusterOfEvent(h.ev).key))
          .sort((a, b) => b.ev.event_ts - a.ev.event_ts);
        setRows(flat);
      })
      .catch((e) => alive && setErr(e instanceof Error ? e.message : t('common.loadFailed')));
    return () => {
      alive = false;
    };
  }, [devices, t]);

  const nameOf = (id: string) => {
    const d = (devices ?? []).find((x) => x.device_id === id);
    return d ? deviceName(d) : id;
  };

  // 按日分組(rows 已 newest-first,組序即日序)
  const days: Array<{ day: string; items: Highlight[] }> = [];
  for (const h of rows ?? []) {
    const k = dayKey(h.ev.event_ts);
    const last = days[days.length - 1];
    if (last && last.day === k) last.items.push(h);
    else days.push({ day: k, items: [h] });
  }

  return (
    <section>
      <header className="dv-page-head">
        <div>
          <h1>{t('events.memoriesTitle')}</h1>
          <p className="text-secondary" style={{ margin: 0 }}>
            {t('events.memoriesIntroBefore', { days: WINDOW_DAYS })}
            <Link to="/events">{t('events.title')}</Link>
            {t('events.memoriesIntroAfter')}
          </p>
        </div>
      </header>

      {err && <p className="error-text">{err}</p>}
      {rows === null && !err && <p className="text-secondary">{t('events.memoriesLoading')}</p>}
      {rows !== null && rows.length === 0 && (
        <div className="card dv-state-card">
          <h2 style={{ margin: 0 }}>{t('events.memoriesEmptyTitle')}</h2>
          <p className="text-secondary" style={{ margin: 0 }}>{t('events.memoriesEmptyHint')}</p>
        </div>
      )}

      {days.map(({ day, items }) => (
        <section key={day} data-testid={`mem-day-${day}`}>
          <h2 className="dv-room-title">{day}<span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginLeft: 8 }}>{t('events.memoriesItemCount', { count: items.length })}</span></h2>
          <div className="dv-grid">
            {items.map((h) => {
              const cl = clusterOfEvent(h.ev);
              return (
                <button
                  key={`${h.deviceId}-${h.ev.event_ts}-${h.ev.event_id}`}
                  className="dv-tile"
                  data-testid="mem-card"
                  onClick={() => navigate(`/playback/${encodeURIComponent(h.deviceId)}?t=${h.ev.event_ts}`)}
                >
                  <div className={`dv-thumb dv-grad-${gradientIndex({ device_id: h.deviceId })}`} style={{ display: 'grid', placeItems: 'center', fontSize: 40 }}>
                    {cl.icon}
                  </div>
                  <div className="dv-tile-body">
                    <div className="dv-tile-title"><span className="dv-name">{cl.label}・{nameOf(h.deviceId)}</span></div>
                    <p className="dv-tile-event text-secondary">{formatRelativeUs(h.ev.event_ts)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </section>
  );
}
